import seoConfig from './seoPages.json';

const JSON_LD_ID = 'seo-jsonld';

const ensureTag = (selector, createTag) => {
    let node = document.head.querySelector(selector);
    const created = !node;

    if (!node) {
        node = createTag();
        document.head.appendChild(node);
    }

    return { node, created };
};

const captureAttribute = (node, attrName) => {
    if (!node) {
        return null;
    }

    return {
        existed: node.hasAttribute(attrName),
        value: node.getAttribute(attrName),
    };
};

const restoreAttribute = (node, attrName, snapshot) => {
    if (!node || !snapshot) {
        return;
    }

    if (snapshot.existed) {
        node.setAttribute(attrName, snapshot.value || '');
        return;
    }

    node.removeAttribute(attrName);
};

const getOriginFromCanonical = (canonicalUrl) => {
    try {
        return new URL(canonicalUrl).origin;
    } catch {
        return window.location.origin;
    }
};

export const buildCanonicalUrl = (origin, path) => {
    if (!path || path === '/') {
        return `${origin}/`;
    }

    return `${origin}${path}`;
};

export const getRouteSeo = (path) => {
    const exact = seoConfig.routes.find((route) => route.path === path);
    if (exact) {
        return exact;
    }

    return seoConfig.routes
        .filter((route) => path.startsWith(`${route.path}/`))
        .sort((left, right) => right.path.length - left.path.length)[0];
};

const buildJsonLd = (jsonLd, origin, canonicalUrl, title, description) => {
    const data = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        ...jsonLd,
        url: canonicalUrl,
    };

    if (data['@type'] === 'Organization') {
        data.logo = `${origin}${seoConfig.ogImagePath}`;
    }

    if (data.mainEntity && data.mainEntity['@type'] === 'Organization') {
        data.mainEntity = {
            ...data.mainEntity,
            url: origin,
        };
    }

    return data;
};

export const applyPageSeo = ({
    title,
    description,
    keywords,
    canonicalUrl,
    siteName = seoConfig.siteName,
    ogType = 'website',
    twitterCard = 'summary_large_image',
    imageUrl,
    imageAlt = title,
    jsonLdId = JSON_LD_ID,
    jsonLdData,
}) => {
    const previousTitle = document.title;
    document.title = title;

    const trackedNodes = [];
    const origin = getOriginFromCanonical(canonicalUrl);
    const resolvedImageUrl = imageUrl || `${origin}${seoConfig.ogImagePath}`;

    const setMeta = (attrName, attrValue, content) => {
        const { node, created } = ensureTag(`meta[${attrName}="${attrValue}"]`, () => {
            const tag = document.createElement('meta');
            tag.setAttribute(attrName, attrValue);
            return tag;
        });
        const previousContent = captureAttribute(node, 'content');
        node.setAttribute('content', content);
        trackedNodes.push({ node, created, attrName: 'content', previousContent });
    };

    const { node: canonicalNode, created: canonicalCreated } = ensureTag('link[rel="canonical"]', () => {
        const tag = document.createElement('link');
        tag.setAttribute('rel', 'canonical');
        return tag;
    });
    const canonicalSnapshot = captureAttribute(canonicalNode, 'href');
    canonicalNode.setAttribute('href', canonicalUrl);
    trackedNodes.push({
        node: canonicalNode,
        created: canonicalCreated,
        attrName: 'href',
        previousContent: canonicalSnapshot,
    });

    setMeta('name', 'description', description);
    if (keywords) {
        setMeta('name', 'keywords', keywords);
    }
    setMeta('name', 'robots', 'index, follow, max-image-preview:large');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:site_name', siteName);
    setMeta('property', 'og:locale', 'en_IN');
    setMeta('property', 'og:image', resolvedImageUrl);
    setMeta('property', 'og:image:alt', imageAlt);
    setMeta('property', 'og:image:type', 'image/png');
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', resolvedImageUrl);
    setMeta('name', 'twitter:image:alt', imageAlt);

    if (jsonLdData) {
        const { node: jsonLdNode, created: jsonLdCreated } = ensureTag(`script#${jsonLdId}`, () => {
            const script = document.createElement('script');
            script.setAttribute('id', jsonLdId);
            script.setAttribute('type', 'application/ld+json');
            return script;
        });
        const previousText = jsonLdNode.textContent;
        jsonLdNode.textContent = JSON.stringify(jsonLdData);
        trackedNodes.push({
            node: jsonLdNode,
            created: jsonLdCreated,
            attrName: 'textContent',
            previousContent: { existed: previousText !== null, value: previousText || '' },
        });
    }

    return () => {
        document.title = previousTitle;

        trackedNodes.forEach(({ node, created, attrName, previousContent }) => {
            if (!node) {
                return;
            }

            if (created) {
                node.remove();
                return;
            }

            if (attrName === 'textContent') {
                node.textContent = previousContent.value;
                return;
            }

            restoreAttribute(node, attrName, previousContent);
        });
    };
};

export const applyRouteSeo = (path) => {
    const route = getRouteSeo(path);
    if (!route) {
        return () => {};
    }

    const origin = window.location.origin;
    const canonicalPath = route.canonicalPath || route.path;
    const canonicalUrl = buildCanonicalUrl(origin, canonicalPath);

    return applyPageSeo({
        title: route.title,
        description: route.description,
        keywords: route.keywords,
        canonicalUrl,
        jsonLdData: buildJsonLd(route.jsonLd, origin, canonicalUrl, route.title, route.description),
    });
};
